
import React, { useState, useEffect } from 'react';
import SearchBar from './components/SearchBar';
import ResultView from './components/ResultView';
import EquipmentView from './components/EquipmentView';
import ProjectsView from './components/ProjectsView';
import InfoPageView from './components/InfoPageView';
import { AppState } from './types';
import { GOOGLE_COLORS } from './constants';
import { Sparkles, Mail, Zap, CheckCircle, ShieldCheck } from 'lucide-react';

const App: React.FC = () => {
  const [appState, setAppState] = useState<AppState>(AppState.SEARCHING);
  const [searchQuery, setSearchQuery] = useState('');
  const [accentColor, setAccentColor] = useState(GOOGLE_COLORS.blue);
  const [showAppsMenu, setShowAppsMenu] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasAiKey, setHasAiKey] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
    checkAiKey();
    checkDeepLink();
  }, []);

  const checkAiKey = async () => {
    try {
      const envKey = process.env.API_KEY;
      const hasEnvKey = !!(envKey && envKey !== 'undefined' && envKey.trim().length > 5);
      
      if (window.aistudio) {
        const bridgeKey = await window.aistudio.hasSelectedApiKey();
        setHasAiKey(hasEnvKey || bridgeKey);
      } else {
        setHasAiKey(hasEnvKey);
      }
    } catch (e) {
      setHasAiKey(false);
    }
  };

  const checkDeepLink = () => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q) {
      setSearchQuery(q);
      setAppState(AppState.RESULT);
    }
  };

  const handleActivateAi = async () => {
    if (window.aistudio) {
      try {
        await window.aistudio.openSelectKey();
        setHasAiKey(true);
        setShowAppsMenu(false);
      } catch (err) {
        console.error("Fout bij selecteren sleutel:", err);
      }
    } else {
      alert("AI Pro functies zijn momenteel alleen beschikbaar in de AI Studio omgeving.");
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setAppState(AppState.RESULT);
    // Update URL zonder herladen voor deelbaarheid
    const newUrl = `${window.location.origin}${window.location.pathname}?q=${encodeURIComponent(query)}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
  };

  const handleBack = () => {
    setAppState(AppState.SEARCHING);
    setSearchQuery('');
    // Reset URL zonder historie te vervuilen
    const newUrl = window.location.origin + window.location.pathname;
    window.history.pushState({}, '', newUrl);
  };

  const navigateTo = (state: AppState) => (e?: React.MouseEvent) => {
    e?.preventDefault();
    setAppState(state);
    setShowAppsMenu(false);
    window.scrollTo(0, 0);
  };

  if (!isInitialized) {
    return <div className="min-h-screen bg-white flex items-center justify-center font-bold text-gray-400 italic">Magazijn wordt geopend...</div>;
  }

  const renderContent = () => {
    switch (appState) {
      case AppState.RESULT: return <ResultView query={searchQuery} onBack={handleBack} />;
      case AppState.EQUIPMENT: return <EquipmentView onBack={handleBack} onItemClick={handleSearch} />;
      case AppState.PROJECTS: return <ProjectsView onBack={handleBack} onItemClick={handleSearch} />;
      case AppState.PRIVACY:
      case AppState.TERMS:
      case AppState.SETTINGS:
      case AppState.ABOUT:
      case AppState.HOW_IT_WORKS:
      case AppState.COMPANY:
        return <InfoPageView type={appState} onBack={handleBack} />;
      default:
        return (
          <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
            <header className="flex justify-end p-4 space-x-3 md:space-x-6 text-sm items-center text-gray-700 font-medium relative">
              {hasAiKey ? (
                <div className="flex items-center space-x-2 bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-600 px-3 py-1.5 rounded-full border border-amber-200 shadow-sm animate-in fade-in zoom-in-95">
                  <Zap className="w-3 h-3 fill-amber-500" />
                  <span className="font-black text-[10px] md:text-xs tracking-tighter">DENIZ PRO AI</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full border border-blue-100 shadow-sm">
                  <ShieldCheck className="w-3 h-3" />
                  <span className="font-bold text-[10px] md:text-xs">DENIZ SMART MODE</span>
                </div>
              )}
              
              <button onClick={navigateTo(AppState.EQUIPMENT)} className="hidden sm:block hover:underline transition-all font-semibold">Apparatuur</button>
              <button onClick={navigateTo(AppState.PROJECTS)} className="hidden sm:block hover:underline transition-all font-semibold">Projecten</button>
              
              {!hasAiKey && (
                <button 
                  onClick={handleActivateAi}
                  className="flex items-center space-x-2 bg-gray-900 text-white px-3 py-1.5 rounded-full hover:bg-black transition-all shadow-md active:scale-95 group"
                >
                  <Sparkles className="w-3 h-3 md:w-4 h-4 group-hover:rotate-12" />
                  <span className="text-[10px] md:text-sm">Upgrade naar Pro</span>
                </button>
              )}
              
              <div className="relative">
                <button 
                  onClick={() => setShowAppsMenu(!showAppsMenu)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
                >
                  <div className="grid grid-cols-3 gap-1 w-4 h-4">
                    {[...Array(9)].map((_, i) => <div key={i} className="bg-gray-400 group-hover:bg-gray-800 rounded-sm"></div>)}
                  </div>
                </button>
                {showAppsMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 shadow-2xl rounded-2xl p-4 z-[100] grid grid-cols-3 gap-4 animate-in fade-in zoom-in-95 duration-200">
                    <button onClick={navigateTo(AppState.HOW_IT_WORKS)} className="flex flex-col items-center p-2 hover:bg-blue-50 rounded-xl transition-colors">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mb-1 font-bold">?</div>
                      <span className="text-[10px] text-center font-bold">Help</span>
                    </button>
                    <button onClick={navigateTo(AppState.EQUIPMENT)} className="flex flex-col items-center p-2 hover:bg-red-50 rounded-xl transition-colors">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-1">📦</div>
                      <span className="text-[10px] text-center font-bold">Voorraad</span>
                    </button>
                    <button onClick={handleActivateAi} className="flex flex-col items-center p-2 hover:bg-yellow-50 rounded-xl transition-colors">
                      <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 mb-1">🔑</div>
                      <span className="text-[10px] text-center font-bold">Sleutel</span>
                    </button>
                  </div>
                )}
              </div>
              
              <button 
                onClick={navigateTo(AppState.ABOUT)}
                className="text-white w-8 h-8 rounded-full flex items-center justify-center font-bold shadow-md hover:opacity-80 transition-all transform hover:scale-105 shrink-0"
                style={{ backgroundColor: accentColor }}
              >
                D
              </button>
            </header>

            <main className="flex-grow flex flex-col items-center pt-12 md:pt-32 px-4 pb-20">
              <div className="mb-8 md:mb-12 select-none text-center group cursor-default max-w-full">
                <h1 className="text-4xl sm:text-6xl md:text-9xl font-bold tracking-[-0.05em] flex flex-wrap justify-center items-baseline leading-tight">
                  <div className="flex transition-transform duration-500 group-hover:-translate-y-1">
                    <span style={{ color: GOOGLE_COLORS.blue }}>D</span>
                    <span style={{ color: GOOGLE_COLORS.red }}>e</span>
                    <span style={{ color: GOOGLE_COLORS.yellow }}>n</span>
                    <span style={{ color: GOOGLE_COLORS.blue }}>i</span>
                    <span style={{ color: GOOGLE_COLORS.green }}>z</span>
                  </div>
                  <div className="flex ml-1 md:ml-2 transition-transform duration-500 delay-75 group-hover:-translate-y-1">
                    <span style={{ color: GOOGLE_COLORS.red }}>V</span>
                    <span style={{ color: GOOGLE_COLORS.yellow }}>e</span>
                    <span style={{ color: GOOGLE_COLORS.blue }}>r</span>
                    <span style={{ color: GOOGLE_COLORS.green }}>h</span>
                    <span style={{ color: GOOGLE_COLORS.blue }}>u</span>
                    <span style={{ color: GOOGLE_COLORS.red }}>u</span>
                    <span style={{ color: GOOGLE_COLORS.yellow }}>r</span>
                    <span style={{ color: GOOGLE_COLORS.green }}>t</span>
                  </div>
                  <span className="text-xl sm:text-3xl md:text-5xl ml-1 text-gray-300 font-light group-hover:text-blue-500 transition-colors">.nl</span>
                </h1>
                <div className="relative mt-2 md:mt-4">
                  <div className="h-[1px] w-16 md:w-24 bg-gradient-to-r from-transparent via-gray-200 to-transparent mx-auto mb-2 md:mb-4"></div>
                  <p className="text-gray-400 font-medium text-[10px] md:text-base uppercase tracking-[0.2em] md:tracking-[0.3em] opacity-80 px-2">
                    {hasAiKey ? '✨ Quantum Enhanced Search Engine ✨' : '🚀 Deniz Personality Engine Active 🚀'}
                  </p>
                </div>
              </div>

              <SearchBar onSearch={handleSearch} />
              
              <div className="mt-12 md:mt-16 max-w-md text-center animate-in fade-in slide-in-from-bottom-2 duration-1000 delay-500">
                <p className="text-gray-400 text-xs md:text-sm italic">
                  {hasAiKey ? 
                    "Je draait nu op de volledige rekenkracht van Deniz' brein. Alle filters zijn los!" : 
                    "Deniz' basis-brein is actief. Zoek iets en ontdek wat hij ervan vindt!"
                  }
                </p>
              </div>
            </main>

            <footer className="bg-[#f2f2f2] text-gray-600 text-[12px] md:text-sm mt-auto">
              <div className="px-4 md:px-8 py-3 border-b border-gray-300 flex flex-col md:flex-row justify-between items-center gap-2 text-center md:text-left">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">Nederland - Backstage Hoofdkantoor</span>
                  <Zap className={`w-3 h-3 ${hasAiKey ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                </div>
                <span className="text-[10px] text-gray-400">
                  Gebouwd door <a href="https://biki90.nl" target="_blank" className="font-bold hover:text-pink-500 transition-colors">Biki Vibecoding</a>. © 2024
                </span>
                <button onClick={navigateTo(AppState.COMPANY)} className="hidden md:inline text-[10px] opacity-40 font-mono tracking-tighter hover:opacity-100 transition-opacity">v smart-hybrid.1.1</button>
              </div>
            </footer>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen selection:bg-blue-100 selection:text-blue-900 font-sans antialiased">
      {renderContent()}
    </div>
  );
};

export default App;
